import type {
  AppScreen,
  AppScreenBinding,
  AppScreenSource,
} from '@taki/data-model';
import { estEffetSocle } from './effets-socle.js';

/**
 * CE QU'UNE DECLARATION D'ECRAN NOMME SANS LE DECRIRE.
 *
 * POURQUOI CE MODULE EXISTE. La declaration est de la DONNEE : un nom de champ,
 * un nom d'agregat, un id de vue. Tout le reste est du CODE, et le fichier le
 * NOMME (`fn`, `habillage`, `catalogue`). Les assembleurs de `@taki/ui` refusent
 * deja un nom introuvable, mais ils le font PORTE PAR PORTE, au moment ou la
 * porte s'assemble : une app dont la troisieme porte cite une fonction absente
 * monte, s'affiche, et ne tombe qu'au clic sur cette porte-la. Ici, on les
 * releve TOUS, a l'import du module de l'app, pour que le refus arrive avant le
 * premier pixel.
 *
 * ET C'EST LE MEME RELEVE QUE LA CI. `scripts/check-app-entities.mjs` lit les
 * memes clefs, sur le meme fichier, et refuse `main` sur un nom absent du
 * registre `FONCTIONS` de l'app. Ce module est la moitie RUNTIME de ce controle :
 * la CI protege la branche, celui-ci protege le poste de developpement, et les
 * deux disent la meme phrase, avec le meme nom.
 *
 * FONCTION PURE, SANS REACT NI SDK : elle ne lit qu'un objet deja valide par
 * Zod, et c'est ce qui la rend testable avec le `taki.screens.json` reel d'une
 * app, sans navigateur.
 */

/** L'espace de noms du registre `FONCTIONS` ou le nom doit se trouver. */
export type EspaceDeFonctions =
  | 'valeurs'
  | 'habillages'
  | 'catalogues'
  | 'predicats'
  | 'formats';

/** Un nom cite par la declaration : ou il est cite, et dans quel espace. */
export interface NomCite {
  espace: EspaceDeFonctions;
  nom: string;
  /** L'endroit de la declaration qui le cite, en clair, pour le message de refus. */
  ou: string;
}

/**
 * LES TROIS NOMS QU'UNE SOURCE DE CHAMP PEUT CITER : sa valeur, son habillage,
 * son catalogue. Ils vivent dans TROIS espaces separes parce qu'une fonction
 * n'a de sens qu'a une place (`FonctionsDeclarees`, `@taki/ui`).
 */
function nomsDeSource(source: AppScreenSource, ou: string): NomCite[] {
  const cites: NomCite[] = [];
  if (source.fn !== undefined) cites.push({ espace: 'valeurs', nom: source.fn, ou });
  if (source.habillage !== undefined) {
    cites.push({ espace: 'habillages', nom: source.habillage, ou });
  }
  if (source.catalogue !== undefined) {
    cites.push({ espace: 'catalogues', nom: source.catalogue, ou });
  }
  return cites;
}

/** Les neuf roles du contrat, plus les roles PROPRES d'une vue custom (`extra`). */
function nomsDeBinding(detail: AppScreenBinding, ou: string): NomCite[] {
  const cites: NomCite[] = [];
  for (const [role, source] of Object.entries(detail)) {
    if (role === 'extra' || source === undefined || source === null) continue;
    /* `colonnes`, `cartes`, `motDuTitre`, `motDuStatut` ne sont pas des sources :
       ils portent des noms de CHAMPS et des mots, jamais de fonction. */
    if (typeof source !== 'object' || Array.isArray(source)) continue;
    cites.push(...nomsDeSource(source as AppScreenSource, `${ou}, role "${role}"`));
  }
  for (const [role, source] of Object.entries(detail.extra ?? {})) {
    cites.push(...nomsDeSource(source, `${ou}, role "${role}"`));
  }
  return cites;
}

/**
 * TOUS LES NOMS QUE CET ECRAN CITE, dans l'ordre ou on les lit.
 *
 * LES HABILLAGES DU SOCLE NE SONT PAS RETIRES ICI, et c'est voulu : ce module ne
 * connait pas le catalogue de `@taki/ui` (`HABILLAGES_SOCLE`), et le recopier en
 * ferait le meme fait a deux endroits. C'est l'appelant qui tient le registre
 * EFFECTIF (celui de l'app, plus le repli du socle) et qui tranche.
 */
export function nomsCites(ecran: AppScreen): NomCite[] {
  const cites: NomCite[] = [];

  for (const [vue, detail] of Object.entries(ecran.vb)) {
    if (detail === undefined) continue;
    cites.push(...nomsDeBinding(detail, `le detail de la vue "${vue}"`));
  }

  for (const colonne of ecran.colonnes ?? []) {
    if (colonne.habillage === undefined) continue;
    cites.push({
      espace: 'habillages',
      nom: colonne.habillage,
      ou: `la colonne "${colonne.champ}"`,
    });
  }

  for (const porte of ecran.portes) {
    for (const [vue, detail] of Object.entries(porte.vb ?? {})) {
      if (detail === undefined) continue;
      cites.push(
        ...nomsDeBinding(detail, `le detail de la vue "${vue}" de la porte "${porte.id}"`),
      );
    }
    for (const autre of porte.filtres?.autres ?? []) {
      if (autre.fn === undefined) continue;
      cites.push({
        espace: 'predicats',
        nom: autre.fn,
        ou: `le filtre "${autre.id}" de la porte "${porte.id}"`,
      });
    }
    for (const stat of porte.stats ?? []) {
      if (stat.habillage === undefined) continue;
      /* UNE STAT N'A PAS DE FICHE, donc son habillage travaille sur la VALEUR
         seule : c'est l'espace `formats`, jamais `habillages` (`statsDe`). */
      cites.push({
        espace: 'formats',
        nom: stat.habillage,
        ou: `la statistique "${stat.id}" de la porte "${porte.id}"`,
      });
    }
  }

  return cites;
}

/** Tous les effets nommes, ceux de l'app comme ceux du socle. */
function tousLesEffetsCites(ecran: AppScreen): { nom: string; ou: string }[] {
  const cites: { nom: string; ou: string }[] = [];
  for (const porte of ecran.portes) {
    for (const action of porte.actions ?? []) {
      /* UNE ACTION D'AJOUT SANS `fn` EST TENUE PAR LE SOCLE (regle G6) : elle ne
         nomme aucun effet, et lui en demander un ferait declarer du vide. */
      if (action.fn === undefined) continue;
      cites.push({ nom: action.fn, ou: `l'action "${action.id}" de la porte "${porte.id}"` });
    }
  }
  return cites;
}

/**
 * Les effets d'action que cet ecran demande A L'APP : l'id de l'action, et le
 * nom de l'effet. Les effets `socle:` n'y sont PAS : c'est le runtime qui les
 * fournit (`effets-socle.ts`), l'app n'a pas a les ecrire.
 */
export function effetsCites(ecran: AppScreen): { nom: string; ou: string }[] {
  return tousLesEffetsCites(ecran).filter((cite) => !estEffetSocle(cite.nom));
}

/** Les effets `socle:` que cet ecran nomme, connus ou non : c'est le montage qui refuse un inconnu. */
export function effetsSocleCites(ecran: AppScreen): { nom: string; ou: string }[] {
  return tousLesEffetsCites(ecran).filter((cite) => estEffetSocle(cite.nom));
}

/** Les portes qui declarent un Space Canvas : leur zone 5 appartient a l'app (G23). */
export function portesCanvas(ecran: AppScreen): string[] {
  return ecran.portes.filter((porte) => porte.canvas !== undefined).map((porte) => porte.id);
}

/** Les fenetres d'app que les gestes de niveau fiche de cet ecran nomment, avec l'action qui les cite. */
export function fenetresCitees(ecran: AppScreen): { nom: string; ou: string }[] {
  const cites: { nom: string; ou: string }[] = [];
  for (const porte of ecran.portes) {
    for (const action of porte.actions ?? []) {
      if (action.fenetre === undefined) continue;
      cites.push({ nom: action.fenetre, ou: `l'action "${action.id}" de la porte "${porte.id}"` });
    }
  }
  return cites;
}
